import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, Loader2, X, FileText, Wand2, CheckCircle2, Sparkles, PlusCircle, Info } from "lucide-react";
import { UNIT_KINDS } from "@/lib/units";
import { toast } from "sonner";

const ALL_UNITS = [...UNIT_KINDS.weight, ...UNIT_KINDS.volume, ...UNIT_KINDS.count];

// UI badge for the match-engine result.
function MatchBadge({ line }) {
  if (line.match_type === "learned") {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-primary/15 text-primary border border-primary/30" title="Remembered from a previous scan">
        <Sparkles className="w-2.5 h-2.5" /> Learned
      </span>
    );
  }
  if (line.match_type === "fuzzy") {
    const pct = Math.round((line.match_confidence || 0) * 100);
    return (
      <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-white/5 text-white/70 border border-white/15" title={`Fuzzy match confidence ${pct}%`}>
        ~ {pct}%
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-white/[0.03] text-white/40 border border-white/10">
      no match
    </span>
  );
}

export default function InvoiceUpload({ open, onOpenChange, ingredients, onApplied }) {
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [lines, setLines] = useState([]);
  const [extracting, setExtracting] = useState(false);
  const [applying, setApplying] = useState(false);
  const [step, setStep] = useState("pick"); // pick | review

  const reset = () => { setFiles([]); setLines([]); setStep("pick"); };

  const onFiles = (e) => {
    const arr = Array.from(e.target.files || []);
    setFiles((prev) => [...prev, ...arr]);
  };
  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const extract = async () => {
    if (!files.length) return;
    setExtracting(true);
    const allLines = [];
    for (const f of files) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/invoices/extract", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        for (const line of (data.lines || [])) {
          const action = line.ingredient_id ? "update" : "create"; // sensible default
          allLines.push({
            ...line,
            source_file: f.name,
            supplier_name: data.supplier_name || "",
            action,
            new_name: line.product_name, // pre-fill for create
          });
        }
      } catch (e) {
        if (e.response?.status === 402) {
          toast.error("Invoice AI is a Pro feature.", {
            action: { label: "Upgrade", onClick: () => { onOpenChange?.(false); navigate("/billing"); } },
          });
          setExtracting(false);
          return;
        }
        toast.error(`${f.name}: ${formatApiErrorDetail(e.response?.data?.detail) || e.message}`);
      }
    }
    setLines(allLines);
    setExtracting(false);
    if (!allLines.length) {
      toast.error("No line items detected. Try a clearer photo or PDF.");
      return;
    }
    setStep("review");
    const learned = allLines.filter((l) => l.match_type === "learned").length;
    const fuzzy = allLines.filter((l) => l.match_type === "fuzzy").length;
    toast.success(`Extracted ${allLines.length} line${allLines.length === 1 ? "" : "s"} • ${learned} learned • ${fuzzy} fuzzy-matched`);
  };

  const setLine = (idx, patch) => setLines((prev) => prev.map((l, i) => i === idx ? { ...l, ...patch } : l));

  const summary = useMemo(() => ({
    total: lines.length,
    update: lines.filter((l) => l.action === "update").length,
    create: lines.filter((l) => l.action === "create").length,
    skip: lines.filter((l) => l.action === "skip").length,
  }), [lines]);

  const apply = async () => {
    setApplying(true);
    try {
      const items = lines.map((l) => ({
        raw_name: l.product_name,
        action: l.action,
        supplier_name: l.supplier_name,
        ingredient_id: l.action === "update" ? (l.ingredient_id || null) : null,
        unit_price: l.unit_price != null ? Number(l.unit_price) : null,
        quantity: l.quantity != null ? Number(l.quantity) : null,
        unit: l.unit || null,
        new_name: l.action === "create" ? (l.new_name || l.product_name) : null,
      }));
      const { data } = await api.post("/invoices/apply", { items });
      const msg = `Updated ${data.updated.length} • Created ${data.created.length} • Learned ${data.mappings_recorded} mapping${data.mappings_recorded === 1 ? "" : "s"}`;
      toast.success(msg);
      onApplied?.();
      onOpenChange(false);
      reset();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setApplying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="invoice-upload">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Wand2 className="w-5 h-5 text-primary" /> Universal Invoice Processor
          </DialogTitle>
        </DialogHeader>

        {step === "pick" && (
          <div className="space-y-4">
            <div className="rounded-2xl border border-dashed border-white/15 p-8 text-center">
              <FileText className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
              <p className="text-sm mb-1">PDFs, screenshots, photos of paper receipts: all handled.</p>
              <p className="text-xs text-muted-foreground mb-4">AI extracts every line and matches it to your pantry. Remembers supplier names so it gets smarter with every scan.</p>
              <label className="inline-block">
                <input
                  type="file"
                  accept="application/pdf,image/*"
                  multiple
                  className="hidden"
                  onChange={onFiles}
                  data-testid="invoice-file-input"
                />
                <Button asChild variant="outline"><span>Add files</span></Button>
              </label>
            </div>
            {files.length > 0 && (
              <div className="space-y-1" data-testid="invoice-file-list">
                {files.map((f, i) => (
                  <div key={i} className="flex items-center justify-between border border-white/10 rounded p-2 text-sm">
                    <span className="truncate">{f.name} <span className="text-muted-foreground text-xs">({(f.size / 1024).toFixed(0)} KB)</span></span>
                    <button onClick={() => removeFile(i)} className="text-muted-foreground hover:text-destructive"><X className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button onClick={extract} disabled={!files.length || extracting} className="gap-2" data-testid="invoice-extract-btn">
                {extracting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
                Extract with AI
              </Button>
            </div>
          </div>
        )}

        {step === "review" && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="label-eyebrow">Review &amp; approve</span>
              <span className="text-muted-foreground">•</span>
              <span data-testid="review-summary-update">Update {summary.update}</span>
              <span className="text-muted-foreground">•</span>
              <span data-testid="review-summary-create">Create {summary.create}</span>
              <span className="text-muted-foreground">•</span>
              <span data-testid="review-summary-skip">Skip {summary.skip}</span>
              <div className="ml-auto flex items-center gap-1 text-[0.65rem] text-muted-foreground">
                <Info className="w-3 h-3" /> Approving a match teaches the app for next time
              </div>
            </div>

            <div className="rounded-2xl border border-white/10 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-xs min-w-[900px]">
                  <thead>
                    <tr className="text-left border-b border-white/10 bg-white/[0.02]">
                      <th className="px-3 py-2 label-eyebrow font-bold">Detected</th>
                      <th className="px-3 py-2 label-eyebrow font-bold">Action</th>
                      <th className="px-3 py-2 label-eyebrow font-bold">Ingredient</th>
                      <th className="px-3 py-2 label-eyebrow font-bold text-right">Qty</th>
                      <th className="px-3 py-2 label-eyebrow font-bold">Unit</th>
                      <th className="px-3 py-2 label-eyebrow font-bold text-right">Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lines.map((l, i) => (
                      <tr key={i} className={`border-b border-white/5 ${l.action === "skip" ? "opacity-50" : ""}`} data-testid={`invoice-line-${i}`}>
                        <td className="px-3 py-2 align-top">
                          <div className="flex items-start gap-2">
                            <MatchBadge line={l} />
                            <div className="min-w-0">
                              <div className="font-medium truncate max-w-[220px]">{l.product_name}</div>
                              <div className="text-muted-foreground text-[0.65rem] truncate">{l.source_file}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-2 align-top">
                          <Select
                            value={l.action}
                            onValueChange={(v) => setLine(i, { action: v })}
                          >
                            <SelectTrigger className="h-7 w-28 text-xs" data-testid={`invoice-action-${i}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="update">Update</SelectItem>
                              <SelectItem value="create">Create new</SelectItem>
                              <SelectItem value="skip">Skip</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-3 py-2 align-top">
                          {l.action === "update" ? (
                            <Select
                              value={l.ingredient_id || ""}
                              onValueChange={(v) => setLine(i, { ingredient_id: v })}
                            >
                              <SelectTrigger className="h-7 w-48 text-xs" data-testid={`invoice-match-${i}`}>
                                <SelectValue placeholder="Pick ingredient…" />
                              </SelectTrigger>
                              <SelectContent>
                                {ingredients.map((ing) => (
                                  <SelectItem key={ing.id} value={ing.id}>{ing.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : l.action === "create" ? (
                            <div className="flex items-center gap-1">
                              <PlusCircle className="w-3 h-3 text-primary shrink-0" />
                              <Input
                                value={l.new_name || ""}
                                onChange={(e) => setLine(i, { new_name: e.target.value })}
                                className="h-7 w-48 text-xs"
                                placeholder="New ingredient name"
                                data-testid={`invoice-newname-${i}`}
                              />
                            </div>
                          ) : (
                            <span className="text-muted-foreground italic">-</span>
                          )}
                        </td>
                        <td className="px-3 py-2 align-top">
                          <Input
                            type="number"
                            step="0.01"
                            value={l.quantity ?? ""}
                            onChange={(e) => setLine(i, { quantity: e.target.value })}
                            className="h-7 w-16 text-right"
                            data-testid={`invoice-qty-${i}`}
                          />
                        </td>
                        <td className="px-3 py-2 align-top">
                          <Select value={l.unit || ""} onValueChange={(v) => setLine(i, { unit: v })}>
                            <SelectTrigger className="h-7 w-20 text-xs" data-testid={`invoice-unit-${i}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {ALL_UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-3 py-2 align-top">
                          <Input
                            type="number"
                            step="0.01"
                            value={l.unit_price ?? ""}
                            onChange={(e) => setLine(i, { unit_price: e.target.value })}
                            className="h-7 w-20 text-right"
                            data-testid={`invoice-price-${i}`}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <DialogFooter>
              <Button variant="ghost" onClick={() => setStep("pick")}>Back</Button>
              <Button
                onClick={apply}
                disabled={applying || summary.total === 0}
                className="gap-2"
                data-testid="invoice-apply-btn"
              >
                {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                Approve &amp; apply
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function InvoiceUploadTrigger({ ingredients, onApplied }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant="outline" className="gap-2" onClick={() => setOpen(true)} data-testid="invoice-upload-btn">
        <Upload className="w-4 h-4" /> Upload invoice
      </Button>
      <InvoiceUpload open={open} onOpenChange={setOpen} ingredients={ingredients} onApplied={onApplied} />
    </>
  );
}
